// Awizacja import — orchestrator service.
//
// One entry point for the FE: `parseAndMap(file)` → parse the .xlsx and resolve
// every candidate against the company/product database + learned aliases.
// `rememberAliases()` persists the confirmed mappings on approve. Persisting
// the transactions themselves stays in the FE (Refine useCreate) for cache
// invalidation.

import type { SupabaseClient } from "../base.ts";
import { AwizacjaParser } from "./AwizacjaParser.ts";
import { AwizacjaMapper } from "./AwizacjaMapper.ts";
import { AwizacjaImportDao } from "./AwizacjaImportDao.ts";
import { AwizacjaAliasStore, type AliasEntry } from "./AwizacjaAliasStore.ts";
import type { AwizacjaImportResult } from "./awizacjaTypes.ts";

export class AwizacjaImportService {
  private readonly parser = new AwizacjaParser();
  private readonly dao: AwizacjaImportDao;
  private readonly aliasStore: AwizacjaAliasStore;

  constructor(sb: SupabaseClient) {
    this.dao = new AwizacjaImportDao(sb);
    this.aliasStore = new AwizacjaAliasStore(sb);
  }

  async parseAndMap(file: File | ArrayBuffer, fileName?: string): Promise<AwizacjaImportResult> {
    const [raws, companies, products, aliases] = await Promise.all([
      this.parser.parse(file, fileName),
      this.dao.loadCompanies(),
      this.dao.loadProducts(),
      this.aliasStore.load(),
    ]);
    return new AwizacjaMapper(companies, products, aliases).mapAll(raws);
  }

  /** Persist confirmed source-name → company mappings for future imports. */
  async rememberAliases(entries: AliasEntry[]): Promise<void> {
    await this.aliasStore.remember(entries);
  }
}
